import type { Plan } from 'grabbitbot-legacy-ui'

// Pure helpers about a plan's subscription period, shared by the payment
// pages and the portal header (no React in here).

type SubscriptionLike = { status?: number | null; expiresAt?: string | null } | null | undefined

function expiryTime(sub: SubscriptionLike): number | null {
  if (!sub?.expiresAt) return null
  const time = new Date(sub.expiresAt).getTime()
  return Number.isFinite(time) ? time : null
}

/** The plan's paid period is over (its subscription has an end date in the
 * past). Shared rule of the 0.14.1 plan-card fix and the payment-method
 * page. */
export function hasPlanEnded(plan: Plan, now = Date.now()): boolean {
  const time = expiryTime(plan.subscription)
  return time !== null && time < now
}

/** Which cause a Checkout session for a plan carries. A plan whose period is
 * over is bought like a new one (`setup`: the new period starts today);
 * `pmchange` would extend the OLD end date by a month, so it is used only for
 * a plan whose period still runs and only when a card is on file. */
export function planSessionCause(method: { setup?: unknown } | null | undefined, endedPeriod: boolean): 'pmchange' | 'setup' {
  return method && method.setup && !endedPeriod ? 'pmchange' : 'setup'
}

/** Owner rule 2026-10-10: a CANCELED subscription (status 6) whose paid
 * period has not ended yet is a paying customer until that date. */
export function isCanceledButActive(sub: SubscriptionLike, now = Date.now()): boolean {
  const time = expiryTime(sub)
  return sub?.status === 6 && time !== null && time > now
}

/** UTC wall-clock text, e.g. `2026-10-18 09:41 UTC` (all statistics and
 * dates of the portal are UTC); the raw value when it is not a date. */
export function utcDateText(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return `${date.toISOString().slice(0, 16).replace('T', ' ')} UTC`
}
